import { useQueryClient } from "@tanstack/react-query";
import { useGrowl } from "PFApp/use_growl";
import NotFoundComponent from "PFComponents/not_found/not_found";
import usePageWrapper from "PFCore/helpers/use_page_wrapper";
import { INTEREST_QUESTIONNAIRES_KEY } from "PFCore/hooks/queries/common/use_interest_questionnaires";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import { useTemplateQuery, useTemplateUpdate } from "../services/queries";
import { BoolFieldName } from "../types";
import EditInterestQuestionsPage from "./page";
import { FormValues } from "./types";

const EditInterestQuestionsController = (): JSX.Element => {
  const { t } = useTranslation(["interestQuestions", "translation"]);
  const queryClient = useQueryClient();
  const growl = useGrowl();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const idInteger = Number(id);

  const WrappedEditInterestQuestionsPage = usePageWrapper(EditInterestQuestionsPage);

  const { data, isLoading: isFetching, error, isRefetching } = useTemplateQuery(idInteger);

  const {
    mutate,
    isLoading: isSubmitting,
    isSuccess
  } = useTemplateUpdate({
    onSuccess: () => {
      history.goBack();
      growl({
        message: t("interestQuestions:edit.saveSuccess")
      });
      queryClient.invalidateQueries({
        queryKey: INTEREST_QUESTIONNAIRES_KEY,
        exact: true
      });
    }
  });

  const handleCancel = () => {
    history.goBack();
  };

  const handleSubmit = (values: FormValues) => {
    const autoToggleValues: { [key in BoolFieldName]?: boolean } = {};
    if (!values.question1 && !values.question2 && !values.question3) {
      autoToggleValues.showQuestions = false;
      autoToggleValues.allowRmToEdit = false;
      autoToggleValues.allowRmToEnable = false;
    }

    mutate({ id: idInteger, data: { ...values, ...autoToggleValues } });
  };

  return (
    <WrappedEditInterestQuestionsPage
      loading={isFetching || isRefetching || isSubmitting || isSuccess}
      errors={error ? [error] : []}
      errorComponent={
        <NotFoundComponent
          message={t("interestQuestions:notFoundMessage")}
          buttonMessage={t("translation:comeBackLater")}
          buttonUrl="/admin/interest_questions"
        />
      }
      data={data}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
    />
  );
};

export default EditInterestQuestionsController;
